import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import pb from '../lib/pocketbaseClient';

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, expensesRes, vendorPaymentsRes, clientPaymentsRes] = await Promise.all([
        pb.collection('accounts_projects').getFullList({ sort: 'name', expand: 'clientId' }),
        pb.collection('project_expenses').getFullList({ expand: 'project' }),
        pb.collection('vendor_payments').getFullList({ expand: 'project' }),
        pb.collection('client_payments').getFullList({ expand: 'project' }),
      ]);
      setProjects(projectsRes);
      setExpenses(expensesRes);
      setVendorPayments(vendorPaymentsRes);
      setClientPayments(clientPaymentsRes);
      applyFilters(projectsRes, expensesRes, vendorPaymentsRes, clientPaymentsRes);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (proj, exp, vp, cp, from = dateFrom, to = dateTo, type = filterType, status = filterStatus) => {
    let filtered = [...proj];

    // Date filter – filter related transactions within date range
    if (from || to) {
      filtered = filtered.filter(p => {
        // Check if project has any transaction in the date range
        const allTrans = [
          ...exp.filter(e => e.project === p.id),
          ...vp.filter(v => v.project === p.id),
          ...cp.filter(c => c.project === p.id),
        ];
        if (allTrans.length === 0) return false;
        const dates = allTrans.map(t => new Date(t.date || t.paymentDate || t.expenseDate));
        const minDate = from ? new Date(from) : new Date(0);
        const maxDate = to ? new Date(to) : new Date(8640000000000000);
        return dates.some(d => d >= minDate && d <= maxDate);
      });
    }

    // Type filter
    if (type !== 'All') {
      filtered = filtered.filter(p => p.type === type);
    }

    // Status filter
    if (status !== 'All') {
      filtered = filtered.filter(p => p.status === status);
    }

    // Compute financials for each project
    const enriched = filtered.map(p => {
      const projectExpenses = exp.filter(e => e.project === p.id);
      const projectVendorPayments = vp.filter(v => v.project === p.id);
      const projectClientPayments = cp.filter(c => c.project === p.id);

      const totalExpenses = projectExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const totalVendorPayments = projectVendorPayments.reduce((sum, v) => sum + (v.amount || 0), 0);
      const totalCost = totalExpenses + totalVendorPayments;
      const totalRevenue = projectClientPayments.reduce((sum, c) => sum + (c.amount || 0), 0);
      const profit = totalRevenue - totalCost;
      const workOrder = p.workOrderValue || 0;
      const balance = workOrder - totalRevenue;

      return {
        ...p,
        totalExpenses,
        totalVendorPayments,
        totalCost,
        totalRevenue,
        profit,
        workOrder,
        balance,
        clientName: p.expand?.clientId?.name || 'N/A',
        profitMargin: totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : '0.0',
      };
    });

    setFilteredProjects(enriched);
  };

  const handleFilterChange = () => {
    applyFilters(projects, expenses, vendorPayments, clientPayments);
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  // Summary totals
  const totalRevenue = filteredProjects.reduce((sum, p) => sum + p.totalRevenue, 0);
  const totalCost = filteredProjects.reduce((sum, p) => sum + p.totalCost, 0);
  const netProfit = totalRevenue - totalCost;
  const totalProjects = filteredProjects.length;

  // Chart data – project-wise revenue vs cost
  const chartData = filteredProjects.map(p => ({
    name: p.name,
    Revenue: p.totalRevenue,
    Cost: p.totalCost,
    Profit: p.profit,
  }));

  // Expense breakdown – aggregate categories across all projects
  const expenseCategories = {};
  expenses.forEach(e => {
    if (filteredProjects.some(p => p.id === e.project)) {
      const cat = e.category || 'other';
      expenseCategories[cat] = (expenseCategories[cat] || 0) + (e.amount || 0);
    }
  });
  const pieData = Object.keys(expenseCategories).map(key => ({
    name: key,
    value: expenseCategories[key],
  }));

  // Export CSV
  const exportCSV = () => {
    const headers = ['Project', 'Client', 'Type', 'Status', 'Work Order', 'Cost', 'Revenue', 'Profit', 'Profit Margin %'];
    const rows = filteredProjects.map(p => [
      p.name,
      p.clientName,
      p.type,
      p.status,
      p.workOrder,
      p.totalCost,
      p.totalRevenue,
      p.profit,
      p.profitMargin,
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PandL_Report_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">📊 Reports &amp; P&amp;L</h1>
          <p className="text-sm text-slate-500">Comprehensive financial overview across all projects</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} className="flex items-center gap-2">
            📥 Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Total Revenue</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Total Cost</p>
            <p className="text-2xl font-bold text-slate-800">{formatCurrency(totalCost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Net Profit / Loss</p>
            <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {netProfit >= 0 ? '+' : ''}{formatCurrency(netProfit)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Total Projects</p>
            <p className="text-2xl font-bold text-slate-800">{totalProjects}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Date:</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          <span className="text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="All">All Types</option>
          <option value="construction">🏗️ Construction</option>
          <option value="interior">🛋️ Interior</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="All">All Status</option>
          <option value="upcoming">🔜 Upcoming</option>
          <option value="ongoing">🟢 Ongoing</option>
          <option value="completed">✅ Completed</option>
          <option value="on-hold">⏸️ On Hold</option>
        </select>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleFilterChange}>Apply Filters</Button>
        <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); setFilterType('All'); setFilterStatus('All'); handleFilterChange(); }}>Clear</Button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>📊 Revenue vs Cost per Project</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `₹${v/1000}k`} />
                  <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#10b981" />
                  <Bar dataKey="Cost" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>🧩 Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project P&L Table */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Project-wise Profit &amp; Loss</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Work Order</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Cost</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Revenue</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Profit</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProjects.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-8 text-center text-slate-500">No projects match the filters.</td></tr>
              ) : (
                filteredProjects.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-6 py-3 font-medium text-slate-800">{p.name}</td>
                    <td className="px-6 py-3">{p.clientName}</td>
                    <td className="px-6 py-3">
                      <Badge variant={p.type === 'construction' ? 'info' : 'purple'}>
                        {p.type === 'construction' ? '🏗️ Construction' : '🛋️ Interior'}
                      </Badge>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={
                        p.status === 'ongoing' ? 'success' :
                        p.status === 'completed' ? 'success' :
                        p.status === 'upcoming' ? 'info' : 'warning'
                      }>
                        {p.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right font-mono">{formatCurrency(p.workOrder)}</td>
                    <td className="px-6 py-3 text-right font-mono">{formatCurrency(p.totalCost)}</td>
                    <td className="px-6 py-3 text-right font-mono text-emerald-600">{formatCurrency(p.totalRevenue)}</td>
                    <td className={`px-6 py-3 text-right font-bold ${p.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {p.profit >= 0 ? '+' : ''}{formatCurrency(p.profit)}
                    </td>
                    <td className={`px-6 py-3 text-right font-bold ${parseFloat(p.profitMargin) >= 10 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {p.profitMargin}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-slate-50 border-t font-semibold">
              <tr>
                <td colSpan="4" className="px-6 py-3 text-right">Totals</td>
                <td className="px-6 py-3 text-right font-mono">{formatCurrency(filteredProjects.reduce((s, p) => s + p.workOrder, 0))}</td>
                <td className="px-6 py-3 text-right font-mono">{formatCurrency(filteredProjects.reduce((s, p) => s + p.totalCost, 0))}</td>
                <td className="px-6 py-3 text-right font-mono text-emerald-600">{formatCurrency(filteredProjects.reduce((s, p) => s + p.totalRevenue, 0))}</td>
                <td className={`px-6 py-3 text-right font-bold ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {netProfit >= 0 ? '+' : ''}{formatCurrency(netProfit)}
                </td>
                <td className="px-6 py-3 text-right">-</td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
